/**
 * PersonaPublishDialog — Faz 2 operatör manuel yayın akışı (çoklu-platform).
 *
 * Style-transfer varyantını Cansu hesaplarından yayınlar. Otonom pipeline'dan
 * BİLİNÇLİ olarak AYRIDIR: faithfulness gate yok (haber-kaynağı yok), operatör
 * incelemecidir.
 *
 * Akış: çoklu kanal seç → (ops.) Wiro görseli üret → "Platformlara çevir"
 * (her platformun FORMAT lugatına uyarla, SES korunur — /persona/adapt) →
 * platform-bazlı düzenlenebilir ÖNİZLEME → "Hepsini Yayınla" (her kanala
 * uyarlanmış metin, now+3dk iptal penceresi).
 */
import { useState, useEffect, useCallback } from "react";
import { Loader2, ImageIcon, X, Rocket, Wand2, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const X_LIMIT = 280;
const CANCEL_MS = 3 * 60 * 1000;

function providerFromIdentifier(id) {
  const s = (id || "").toLowerCase();
  if (s.includes("twitter") || s === "x") return "twitter";
  if (s.includes("linkedin")) return "linkedin";
  if (s.includes("instagram")) return "instagram";
  if (s.includes("youtube")) return "youtube";
  if (s.includes("devto")) return "devto";
  return s || "twitter";
}

export default function PersonaPublishDialog({ open, onOpenChange, generationId, variant }) {
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);     // çoklu kanal id
  const [baseContent, setBaseContent] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [genImgLoading, setGenImgLoading] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [adapting, setAdapting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // adaptations: { [channelId]: { platform, content } } — düzenlenebilir önizleme
  const [adaptations, setAdaptations] = useState(null);

  // Reset + prefill on open
  useEffect(() => {
    if (open) {
      setBaseContent(variant?.content || "");
      setImageUrl(null); setImagePrompt(""); setImgError(false);
      setAdaptations(null); setSelectedIds([]);
    }
  }, [open, variant]);

  // Load connected channels
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setChannelsLoading(true);
      try {
        const res = await api.get(`${API}/postiz/integrations`);
        const items = (res.data?.items || []).filter((c) => !c.disabled);
        if (cancelled) return;
        setChannels(items);
        // varsayılan: X kanalını seçili getir
        const x = items.find((c) => /x|twitter/i.test(c.identifier || ""));
        if (x) setSelectedIds([x.id]);
      } catch {
        if (!cancelled) toast.error("Kanallar yüklenemedi");
      } finally {
        if (!cancelled) setChannelsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const selectedChannels = channels.filter((c) => selectedIds.includes(c.id));

  const toggleChannel = (c) => {
    setAdaptations(null); // seçim değişti → önizlemeyi sıfırla
    setSelectedIds((prev) =>
      prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id]
    );
  };

  const handleGenerateImage = useCallback(async () => {
    setGenImgLoading(true);
    try {
      const res = await api.post(`${API}/persona/generate-image`, {
        prompt: (imagePrompt || "").trim(),
        topic: (baseContent || "").slice(0, 200),
        aspect_ratio: "16:9",
        generation_id: generationId,
      });
      setImageUrl(res.data?.image_url || null); setImgError(false);
      toast.success("Görsel üretildi");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Görsel üretilemedi"));
    } finally {
      setGenImgLoading(false);
    }
  }, [imagePrompt, baseContent, generationId]);

  // Platformlara çevir (FORMAT uyarlaması, ses korunur)
  const handleAdapt = useCallback(async () => {
    if (!selectedChannels.length) { toast.error("En az bir kanal seç"); return; }
    if (!baseContent.trim()) { toast.error("İçerik boş"); return; }
    const platforms = [...new Set(selectedChannels.map((c) => providerFromIdentifier(c.identifier)))];
    setAdapting(true);
    try {
      const res = await api.post(`${API}/persona/adapt`, { content: baseContent.trim(), platforms });
      const byPlatform = {};
      (res.data?.adaptations || []).forEach((a) => { byPlatform[a.platform] = a.content; });
      const next = {};
      selectedChannels.forEach((c) => {
        const pl = providerFromIdentifier(c.identifier);
        next[c.id] = { platform: pl, content: byPlatform[pl] ?? baseContent.trim() };
      });
      setAdaptations(next);
      toast.success(`${platforms.length} platforma uyarlandı — önizleyip düzenle`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Uyarlama başarısız"));
    } finally {
      setAdapting(false);
    }
  }, [selectedChannels, baseContent]);

  const setCardContent = (channelId, val) =>
    setAdaptations((prev) => ({ ...prev, [channelId]: { ...prev[channelId], content: val } }));

  // Hepsini yayınla
  const handlePublishAll = useCallback(async () => {
    if (!adaptations) return;
    setPublishing(true);
    const postIds = [];
    let okCount = 0;
    try {
      for (const c of selectedChannels) {
        const ad = adaptations[c.id];
        if (!ad) continue;
        const provider = ad.platform;
        const content = (ad.content || "").trim();
        if (!content) continue;
        if (["twitter", "x"].includes(provider) && content.length > X_LIMIT) {
          toast.error(`${c.name || provider}: ${X_LIMIT} karakteri aşıyor, kısalt`);
          continue;
        }
        try {
          const res = await api.post(`${API}/persona/publish`, {
            generation_id: generationId,
            content,
            integration_id: c.id,
            provider,
            image_url: imageUrl || undefined,
          });
          if (res.data?.postiz_post_id) postIds.push(res.data.postiz_post_id);
          okCount++;
        } catch (e) {
          const d = e?.response?.data?.detail;
          toast.error(`${c.name || provider}: ${typeof d === "string" ? d : (d?.message || "yayın hatası")}`);
        }
      }
      if (okCount > 0) {
        onOpenChange(false);
        toast.success(`${okCount} platforma yayınlandı — 3 dk içinde geri alabilirsiniz`, {
          duration: CANCEL_MS,
          action: postIds.length ? {
            label: "Hepsini geri al",
            onClick: async () => {
              await Promise.allSettled(postIds.map((pid) => api.post(`${API}/postiz/posts/${pid}/cancel`)));
              toast.success("Yayınlar geri alındı");
            },
          } : undefined,
        });
      }
    } finally {
      setPublishing(false);
    }
  }, [adaptations, selectedChannels, generationId, imageUrl, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cansu hesabından yayınla</DialogTitle>
          <DialogDescription>
            Operatör yayını — otonom pipeline'dan ayrı. Her platform kendi formatına uyarlanır (ses korunur). Yayından önce gözden geçirin.
          </DialogDescription>
        </DialogHeader>

        {/* Kanallar (çoklu) */}
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground">Kanallar (birden fazla seçebilirsin)</label>
          {channelsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor...
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {channels.map((c) => {
                const active = selectedIds.includes(c.id);
                return (
                  <button key={c.id} onClick={() => toggleChannel(c)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-all",
                      active ? "border-violet-500 bg-violet-500/10 text-violet-200"
                             : "border-border text-muted-foreground hover:border-violet-400/50"
                    )}>
                    {active && <Check className="h-3.5 w-3.5" />}
                    {c.picture && <img src={c.picture} alt="" className="h-4 w-4 rounded-full" />}
                    {c.name || c.identifier}
                    <span className="text-[10px] opacity-60">{providerFromIdentifier(c.identifier)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Base içerik */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Kaynak içerik</label>
          <textarea value={baseContent} onChange={(e) => { setBaseContent(e.target.value); setAdaptations(null); }}
            rows={4}
            className="w-full rounded-lg border border-border bg-background p-3 text-sm resize-none focus:outline-none focus:border-violet-500" />
        </div>

        {/* Görsel (ops., tüm platformlara) */}
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground">Görsel (opsiyonel — tüm platformlara)</label>
          {imageUrl ? (
            <div className="space-y-1">
              <div className="relative inline-block">
                {imgError ? (
                  <a href={imageUrl} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-xs text-violet-400 underline break-all max-w-[280px]">
                    <ImageIcon className="h-3.5 w-3.5 shrink-0" /> Görsel üretildi — önizlenemedi, linki aç
                  </a>
                ) : (
                  <img src={imageUrl} alt="üretilen görsel" onError={() => setImgError(true)}
                    className="max-h-40 rounded-lg border border-border" />
                )}
                <button onClick={() => { setImageUrl(null); setImgError(false); }}
                  className="absolute top-1 right-1 bg-black/60 rounded-full p-1">
                  <X className="h-3 w-3 text-white" />
                </button>
              </div>
              <div className="text-[10px] text-muted-foreground">Görsel tüm seçili platformlara eklenecek.</div>
            </div>
          ) : (
            <div className="flex gap-2">
              <input value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)}
                placeholder="Görsel istemi (boş bırak → konudan otomatik üretilir)"
                className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-violet-500" />
              <Button variant="outline" size="sm" onClick={handleGenerateImage} disabled={genImgLoading} className="gap-1.5 shrink-0">
                {genImgLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} Görsel üret
              </Button>
            </div>
          )}
        </div>

        {/* Platformlara çevir */}
        {!adaptations && (
          <Button onClick={handleAdapt} disabled={adapting || !selectedChannels.length || !baseContent.trim()}
            className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white w-full">
            {adapting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Platformlara çevir & önizle ({selectedChannels.length})
          </Button>
        )}

        {/* Platform-bazlı önizleme (düzenlenebilir) */}
        {adaptations && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">Platform önizlemeleri — düzenleyebilirsin:</div>
            {selectedChannels.map((c) => {
              const ad = adaptations[c.id];
              if (!ad) return null;
              const isX = ["twitter", "x"].includes(ad.platform);
              const over = isX && (ad.content || "").length > X_LIMIT;
              return (
                <div key={c.id} className="rounded-lg border border-border p-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      {c.picture && <img src={c.picture} alt="" className="h-4 w-4 rounded-full" />}
                      {c.name || c.identifier}
                      <span className="text-[10px] text-violet-300 bg-violet-500/10 px-1.5 py-0.5 rounded">{ad.platform}</span>
                    </span>
                    <span className={cn("text-[11px]", over ? "text-red-400" : "text-muted-foreground")}>
                      {(ad.content || "").length}{isX ? `/${X_LIMIT}` : ""}
                    </span>
                  </div>
                  <textarea value={ad.content} onChange={(e) => setCardContent(c.id, e.target.value)}
                    rows={ad.platform === "devto" || ad.platform === "blog" ? 7 : 4}
                    className={cn("w-full rounded-lg border bg-background p-2.5 text-sm resize-none focus:outline-none",
                      over ? "border-red-500/60" : "border-border focus:border-violet-500")} />
                  {imageUrl && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <ImageIcon className="h-3 w-3" /> görsel ekli
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={publishing}>Vazgeç</Button>
          <div className="flex gap-2">
            {adaptations && (
              <Button variant="outline" onClick={() => setAdaptations(null)} disabled={publishing}>← Düzenle</Button>
            )}
            <Button onClick={handlePublishAll}
              disabled={publishing || !adaptations}
              className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white">
              {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
              Hepsini Yayınla ({selectedChannels.length})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
